import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  GitPullRequest,
  Mail,
  Calendar,
  CheckSquare,
  Clock,
  BarChart2,
  LogOut,
  LayoutGrid,
  ChevronLeft,
  Table2,
  FileText,
  FileSignature,
  QrCode,
  LayoutTemplate,
  Settings,
  Mic,
  Fish,
  Anchor,
  Scale,
  Receipt,
  MessagesSquare,
  Ship,
  ChevronsUpDown,
  Check,
  Spline,
  Newspaper,
  HandHeart,
  Contact,
  Wallet,
  Timer,
  ClipboardList,
  FolderKanban,
  NotebookPen,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { getCurrentUser, logout, isSuperAdmin, canAccessSignal, getMyCompanies, switchCompany } from '@/lib/auth';
import { useLanguage, useT } from '@/lib/LanguageContext';
import api from '@/lib/api';
import { useModules, hasModule } from '@/lib/modules';

// ── Nav structure ─────────────────────────────────────────────────────────────
// Built as a function so labels update when language changes.

// Stagehead's working waterfront: what comes in (The Catch), where the work is done (The Stage),
// what's settled (The Tally), and the stories that go out (The Yarns).
const buildNav = (t, twineCount = 0) => [
  {
    type: 'link',
    name: t('nav.dashboard'),
    path: '/dashboard',
    icon: LayoutDashboard,
    testId: 'sidebar-nav-dashboard',
  },
  {
    type: 'group',
    key: 'catch',
    label: t('nav.catch_group'),
    icon: Fish,
    children: [
      { name: t('nav.contacts'),       path: '/contacts',               icon: Users,          testId: 'sidebar-nav-contacts' },
      { name: t('nav.pipeline_board'), path: '/pipeline',               icon: GitPullRequest, testId: 'sidebar-nav-pipeline', module: 'pipeline' },
      { name: t('nav.opportunities'),  path: '/pipeline/opportunities', icon: Table2,         testId: 'sidebar-nav-opportunities', module: 'pipeline' },
      { name: t('nav.twine'),          path: '/twine',                  icon: Spline,         testId: 'sidebar-nav-twine', count: twineCount, module: 'twine' },
    ],
  },
  {
    type: 'group',
    key: 'stage',
    label: t('nav.stage_group'),
    icon: Anchor,
    children: [
      { name: t('nav.projects'),   path: '/projects',   icon: FolderKanban, testId: 'sidebar-nav-projects' },
      { name: t('nav.task_board'), path: '/tasks',      icon: CheckSquare, testId: 'sidebar-nav-tasks' },
      { name: t('nav.notes'),      path: '/notes',      icon: NotebookPen, testId: 'sidebar-nav-notes' },
      { name: t('nav.documents'),  path: '/documents',  icon: FileText,    testId: 'sidebar-nav-documents' },
      { name: t('nav.signups'),    path: '/signups',    icon: HandHeart,   testId: 'sidebar-nav-signups', module: 'signups' },
      { name: t('nav.scheduling'), path: '/scheduling', icon: Clock,       testId: 'sidebar-nav-scheduling', comingSoon: true },
    ],
  },
  {
    type: 'group',
    key: 'tally',
    label: t('nav.tally_group'),
    icon: Scale,
    children: [
      { name: t('nav.esignature'), path: '/esignature', icon: FileSignature, testId: 'sidebar-nav-esignature', module: 'esign' },
      { name: t('nav.quotes'),     path: '/quotes',     icon: ClipboardList, testId: 'sidebar-nav-quotes', module: 'quotes' },
      { name: t('nav.invoices'),   path: '/invoices',   icon: Receipt,       testId: 'sidebar-nav-invoices', module: 'invoices' },
      { name: t('nav.time'),       path: '/time',       icon: Timer,         testId: 'sidebar-nav-time', module: 'time' },
      { name: t('nav.expenses'),   path: '/expenses',   icon: Wallet,        testId: 'sidebar-nav-expenses', module: 'expenses' },
    ],
  },
  {
    type: 'group',
    key: 'yarns',
    label: t('nav.yarns_group'),
    icon: MessagesSquare,
    children: [
      { name: t('nav.yarns'), path: '/yarns', icon: MessagesSquare, testId: 'sidebar-nav-yarns' },
      { name: t('nav.bulletin'), path: '/bulletin', icon: Newspaper, testId: 'sidebar-nav-bulletin', module: 'bulletin' },
      { name: t('nav.interviews'), path: '/interviews', icon: Mic, testId: 'sidebar-nav-interviews', module: 'interviews' },
      { name: t('nav.social_calendar'), path: '/social',    icon: Calendar, testId: 'sidebar-nav-social',    signal: true, module: 'social' },
      { name: t('nav.campaigns'),       path: '/sequences', icon: Mail,     testId: 'sidebar-nav-sequences', signal: true, module: 'campaigns' },
      { name: t('nav.qrcodes'),         path: '/qrcodes',   icon: QrCode,   testId: 'sidebar-nav-qrcodes', module: 'qrcodes' },
    ],
  },
  {
    type: 'link',
    name: t('nav.reporting'),
    path: '/reporting',
    icon: BarChart2,
    testId: 'sidebar-nav-reporting',
  },
];

const buildManage = (t) => [
  { name: t('nav.clients'), path: '/clients', icon: Users, testId: 'sidebar-nav-clients', module: 'clients' },
  { name: t('nav.my_card'), path: '/my-card', icon: Contact, testId: 'sidebar-nav-my-card', module: 'cards' },
  { name: t('nav.configuration'), path: '/settings/team', icon: Settings, testId: 'sidebar-nav-configuration' },
  { name: 'Email Templates', path: '/email-templates', icon: LayoutTemplate, testId: 'sidebar-nav-email-templates' },
];

// ── NavLink ───────────────────────────────────────────────────────────────────

const NavLink = ({ item, collapsed, isActive, indented = false, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.disabled ? '#' : item.path}
      onClickCapture={item.disabled ? (e) => e.preventDefault() : undefined}
      aria-disabled={item.disabled || undefined}
      data-testid={item.testId}
      title={collapsed ? item.name : undefined}
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg transition-all duration-150 relative"
      style={{
        padding: collapsed ? '9px 12px' : indented ? '7px 16px 7px 20px' : '9px 16px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        backgroundColor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
        color: isActive ? 'white' : 'rgba(255,255,255,0.65)',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {isActive && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-r"
          style={{ backgroundColor: 'hsl(var(--sidebar-active))' }}
        />
      )}
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="font-medium text-sm flex-1 truncate">{item.name}</span>
          {item.count > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 bg-[#A62B1F] text-white" title={`${item.count} to review`}>
              {item.count > 99 ? '99+' : item.count}
            </span>
          )}
          {item.comingSoon && (
            <span
              className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
            >
              {item.soonLabel || 'Soon'}
            </span>
          )}
        </>
      )}
    </Link>
  );
};

// ── GroupLabel — shown only when expanded ─────────────────────────────────────

const GroupLabel = ({ label, icon: Icon }) => (
  <div
    className="flex items-center gap-2 px-4 pt-4 pb-1"
    style={{ color: 'rgba(255,255,255,0.38)' }}
  >
    <Icon className="w-3 h-3 flex-shrink-0" />
    <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
  </div>
);

// ── Sidebar ───────────────────────────────────────────────────────────────────

export const Sidebar = ({ onNavigate }) => {
  const location = useLocation();
  const user = getCurrentUser();
  const superAdmin = isSuperAdmin();
  const displayName = user?.full_name || user?.name || null;
  const userInitials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'SH';

  const { lang, toggle } = useLanguage();
  const t = useT();
  // Modules this company has switched on (Stagehead sets these in Companies)
  const modules = useModules();
  const twineOn = hasModule(modules, 'twine');
  // The Twine: how many suggestions are waiting for review
  const [twineCount, setTwineCount] = useState(0);
  useEffect(() => {
    if (!twineOn) return undefined;
    const load = () => api.get('/api/twine/review/count').then((r) => setTwineCount(r.data?.count || 0)).catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener('twine-changed', load);
    return () => { clearInterval(timer); window.removeEventListener('twine-changed', load); };
  }, [twineOn]);
  // Until the module list has loaded, show only the core so switched-off modules never flash up
  const NAV = buildNav(t, twineCount);

  // One login, many companies: pick which company you're working in
  const companies = getMyCompanies();
  const currentCompanyId = user?.company_id;
  const currentCompany = companies.find((c) => c.id === currentCompanyId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const pick = async (id) => {
    if (id === currentCompanyId) { setPickerOpen(false); return; }
    setSwitching(id);
    try { await switchCompany(id); } catch (e) { setSwitching(null); }
  };
  const MANAGE = buildManage(t);

  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 1280);
  const signalAllowed = canAccessSignal();

  useEffect(() => {
    const handler = () => setCollapsed(prev => {
      if (window.innerWidth < 1024) return true;
      if (window.innerWidth >= 1280) return false;
      return prev;
    });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const sidebarWidth = collapsed ? '72px' : '260px';

  const isActive = (path, exact = false) =>
    location.pathname === path || (!exact && location.pathname.startsWith(path + '/'));

  return (
    <div
      className="flex flex-col h-screen border-r flex-shrink-0 transition-all duration-200"
      style={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        backgroundColor: 'hsl(var(--sidebar))',
        borderColor: 'hsl(var(--sidebar-border))',
        color: 'hsl(var(--sidebar-foreground))',
      }}
    >
      {/* Logo + collapse toggle */}
      <div
        className="flex items-center border-b flex-shrink-0"
        style={{
          borderColor: 'hsl(var(--sidebar-border))',
          padding: collapsed ? '20px 12px' : '20px 24px',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: '72px',
        }}
      >
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            title="Expand sidebar"
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/10 transition-colors"
          >
            <img src="/stagehead-os-mark.svg" alt="Stagehead OS" className="w-8 h-8 rounded-lg" />
          </button>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <img src="/stagehead-os-mark.svg" alt="" className="w-10 h-10 rounded-[10px]" />
              <span className="flex flex-col items-end leading-none">
                <span className="font-display text-[22px] text-white">Stagehead</span>
                <span className="text-[9px] font-bold mt-1 -mr-[0.4em]" style={{ letterSpacing: '0.4em', color: '#F0B56E' }}>OS</span>
              </span>
            </div>
            <button
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar"
              className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/10 transition-colors text-white/50 hover:text-white/90"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Scrollable nav area */}
      {companies.length > 1 && (
        <div className="relative px-2 pt-2" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
          <button
            onClick={() => (collapsed ? setCollapsed(false) : setPickerOpen((o) => !o))}
            title={collapsed ? (currentCompany?.name || 'Switch company') : undefined}
            className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-white/10 transition-colors"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', justifyContent: collapsed ? 'center' : 'flex-start' }}
          >
            <span className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0" style={{ backgroundColor: '#A62B1F', color: '#fff' }}>
              {(currentCompany?.name || '?').slice(0, 1)}
            </span>
            {!collapsed && (
              <>
                <span className="flex-1 min-w-0">
                  <span className="block text-[10px] uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.45)' }}>Working in</span>
                  <span className="block text-sm font-semibold text-white truncate">{currentCompany?.name || 'Company'}</span>
                </span>
                <ChevronsUpDown className="w-4 h-4 shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} />
              </>
            )}
          </button>
          {pickerOpen && !collapsed && (
            <div className="absolute left-2 right-2 mt-1 z-50 rounded-lg border shadow-lg overflow-hidden" style={{ backgroundColor: '#1E2A33', borderColor: 'rgba(255,255,255,0.12)' }}>
              {companies.map((c) => (
                <button key={c.id} onClick={() => pick(c.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-white/10" style={{ color: '#fff' }}>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.45)' }}>{c.role}</span>
                  {c.id === currentCompanyId ? <Check className="w-4 h-4" /> : switching === c.id ? <span className="text-[11px]">…</span> : <span className="w-4" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto overflow-x-hidden" style={{ padding: collapsed ? '8px 8px' : '8px 8px' }}>

        {/* Super admin */}
        {superAdmin && (
          <NavLink
            item={{ name: t('nav.companies'), path: '/companies', icon: LayoutGrid, testId: 'sidebar-nav-companies' }}
            collapsed={collapsed}
            onClick={onNavigate}
            isActive={location.pathname === '/companies'}
          />
        )}

        {/* Main nav groups */}
        {NAV
          // Social Calendar and Campaigns stay behind the existing Signal permission
          .map(entry => (entry.type === 'group' ? { ...entry, children: entry.children.filter(c => (!c.signal || signalAllowed) && (!c.module || hasModule(modules, c.module))) } : entry))
          .filter(entry => entry.type !== 'group' || entry.children.length > 0)
          .map((entry, i) => {
          if (entry.type === 'link') {
            return (
              <NavLink
                key={entry.path}
                item={entry}
                collapsed={collapsed}
                isActive={isActive(entry.path)}
              />
            );
          }

          // Group
          const anyChildActive = entry.children.some(c => isActive(c.path, true));

          if (collapsed) {
            // Collapsed: show each child icon individually, no label
            return (
              <div key={entry.label} className="space-y-0.5">
                {entry.children.map(child => (
                  <NavLink
                    key={child.path}
                    item={child}
                    collapsed={true}
                    isActive={isActive(child.path, true)}
                  />
                ))}
              </div>
            );
          }

          return (
            <div key={entry.label}>
              <GroupLabel label={entry.label} icon={entry.icon} />
              <div className="space-y-0.5 mt-0.5">
                {entry.children.map(child => (
                  <NavLink
                    key={child.path}
                    item={child}
                    collapsed={false}
                    isActive={isActive(child.path, true)}
                    indented
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Manage group */}
        <div className="border-t mt-3" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        {!collapsed && (
          <GroupLabel label={t('nav.manage_group')} icon={Ship} />
        )}
        <div className="space-y-0.5 mt-0.5">
          {MANAGE.filter(item => !item.module || hasModule(modules, item.module)).map(item => (
            <NavLink
              key={item.path}
              item={item}
              collapsed={collapsed}
              isActive={item.path === '/settings/team' ? location.pathname.startsWith('/settings') : location.pathname.startsWith(item.path)}
              indented={!collapsed}
            />
          ))}
        </div>
      </nav>

      {/* User section */}
      <div
        className="border-t flex-shrink-0"
        style={{
          borderColor: 'hsl(var(--sidebar-border))',
          padding: collapsed ? '12px 8px' : '12px 16px',
        }}
      >
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-[#A62B1F] text-white text-xs">{userInitials}</AvatarFallback>
            </Avatar>
            {/* Language toggle — compact */}
            <button
              onClick={toggle}
              title={t('nav.language')}
              className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-white/10 transition-colors text-white/60 hover:text-white text-[10px] font-bold tracking-wide"
            >
              {lang === 'en' ? 'ES' : 'EN'}
            </button>
            <button
              onClick={logout}
              title={t('nav.logout')}
              className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-white/10 transition-colors text-white/60 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <Avatar>
                <AvatarFallback className="bg-[#A62B1F] text-white">{userInitials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{displayName || user?.email || 'User'}</p>
                <p className="text-xs truncate" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {user?.email || ''}
                </p>
              </div>
            </div>
            {/* Language toggle row */}
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs text-white/40">{t('nav.language')}</span>
              <button
                onClick={toggle}
                className="flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border transition-colors"
                style={{
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.8)',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                }}
              >
                <span style={{ opacity: lang === 'en' ? 1 : 0.4 }}>EN</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span style={{ opacity: lang === 'es' ? 1 : 0.4 }}>ES</span>
              </button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              onClick={logout}
              data-testid="sidebar-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('nav.logout')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
